/**
 * Cell-level parsing and validation of the uploaded sales sheet.
 *
 * Pure: takes the ImportSheet produced by utils/importFileParser plus the user's column
 * mapping, returns typed rows and a list of per-row errors that the preview screen shows
 * before anything is written.
 */

import { type ImportCell, type ImportSheet } from '../importFileParser'
import {
  cellToString,
  parseImportDate,
  parseImportQuantity,
  readCell,
} from '../importCells'
import {
  type ParseSalesRowsResult,
  type ParsedSalesRow,
  type SalesImportColumnMapping,
  type SalesImportRowError,
} from './types'

/**
 * Normalize a phone number to digits, so `+7 (777) 123-45-67`, `87771234567` and
 * `77771234567` are one client. An 11-digit number starting with 8 is the local way of
 * writing the +7 country code (KZ/RU).
 */
export const normalizePhone = (raw: ImportCell): string | null => {
  const digits = cellToString(raw).replace(/\D/g, '')
  if (!digits) return null
  if (digits.length === 11 && digits.startsWith('8')) return `7${digits.slice(1)}`
  return digits
}


/**
 * Parse every data row of the sheet (row 0 is the header). Each row lands either in
 * `rows` or in `errors` — never both, and never silently dropped.
 */
export const parseSalesRows = (
  sheet: ImportSheet,
  mapping: SalesImportColumnMapping
): ParseSalesRowsResult => {
  const rows: ParsedSalesRow[] = []
  const errors: SalesImportRowError[] = []

  for (let index = 1; index < sheet.rows.length; index++) {
    const row = sheet.rows[index]
    const rowNumber = sheet.rowNumbers[index]

    const articleCell = readCell(row, mapping.article)
    const article = cellToString(articleCell)
    if (!article) {
      errors.push({ rowNumber, code: 'MISSING_ARTICLE', value: '' })
      continue
    }

    const quantityCell = readCell(row, mapping.quantity)
    if (cellToString(quantityCell) === '') {
      errors.push({ rowNumber, code: 'MISSING_QUANTITY', value: '' })
      continue
    }
    const quantity = parseImportQuantity(quantityCell)
    if (quantity === null) {
      errors.push({ rowNumber, code: 'INVALID_QUANTITY', value: cellToString(quantityCell) })
      continue
    }

    const dateCell = readCell(row, mapping.date)
    if (cellToString(dateCell) === '') {
      errors.push({ rowNumber, code: 'MISSING_DATE', value: '' })
      continue
    }
    const date = parseImportDate(dateCell, sheet.date1904)
    if (date === null) {
      errors.push({ rowNumber, code: 'INVALID_DATE', value: cellToString(dateCell) })
      continue
    }

    const paymentMethodKey = cellToString(readCell(row, mapping.paymentMethod)) || null

    rows.push({
      rowNumber,
      article,
      quantity,
      date,
      clientKey: normalizePhone(readCell(row, mapping.client)),
      paymentMethodKey,
    })
  }

  return { rows, errors }
}

// Moved to utils/importCells.ts; re-exported so existing sales import call sites are unchanged.
export { parseImportDate, parseImportQuantity }
